// @ts-ignore
import {_, _any} from "i18n";
import {Field, Form} from "react-final-form";
import {Badge} from "ui-kit";
import {CategoryEnum, getTeamStatCategories} from "./definition";
import {ValidationErrors} from "final-form";
import "./TeamStatsExport.css";
import {ProcessingLevelEnum} from "../../types";

interface TeamStatsExportProps {
	category: CategoryEnum;
	processingLevel?: ProcessingLevelEnum;
	showExpectedGoals: boolean;
	onExportTeamStats: (format: "csv" | "xlsx" | "json", categories: CategoryEnum[], periods: string[]) => void;
	gamePeriods: { key: string; label: string; }[];
	selectedPeriods: string[];
}

interface Option {
	key: string;
	value: string;
	label: string;
}

interface FormValues {
	format: "csv" | "xlsx";
	summary: boolean;
	offensive: boolean;
	defensive: boolean;
	distribution: boolean;
	advancedMetrics: boolean;
	periods: boolean[];
}

export function TeamStatsExport({ category, processingLevel, showExpectedGoals, gamePeriods, selectedPeriods, onExportTeamStats }: TeamStatsExportProps) {
	const availableCategories = getTeamStatCategories(processingLevel)
		.filter((statCategory) => statCategory !== CategoryEnum.AdvancedMetrics || showExpectedGoals);

	const onSubmit = (values: FormValues) => {
		const categories: CategoryEnum[] = [];

		if (values.summary) {
			categories.push(CategoryEnum.Summary);
		}

		if (values.offensive) {
			categories.push(CategoryEnum.Offensive);
		}

		if (values.defensive) {
			categories.push(CategoryEnum.Defensive);
		}

		if (values.distribution) {
			categories.push(CategoryEnum.Distribution);
		}

		if (values.advancedMetrics) {
			categories.push(CategoryEnum.AdvancedMetrics);
		}

		onExportTeamStats(
			values.format,
			categories,
			gamePeriods
				.filter((gp, index) => values.periods[index])
				.map((gp) => gp.key)
		);
	}

	// @ts-ignore
	const onValidate = (values: FormValues): ValidationErrors | Promise<ValidationErrors> => {
		let categoriesCount = 0;
		const errors: any = {};

		Object.values(CategoryEnum).forEach((cat) => {
			if (values[cat] === true) {
				categoriesCount++;
			}
		});

		if (categoriesCount === 0) {
			errors.format = "Invalid form";
		}

		if (values.periods.filter((p) => p).length === 0) {
			errors.format = "Invalid form";
		}

		return errors;
	}

	const exportFormValues = {
		format: "csv",
		summary: category === CategoryEnum.Summary,
		offensive: category === CategoryEnum.Offensive,
		defensive: category === CategoryEnum.Defensive,
		distribution: category === CategoryEnum.Distribution,
		advancedMetrics: category === CategoryEnum.AdvancedMetrics,
		periods: gamePeriods.map((gp) => selectedPeriods.includes(gp.key)),
	} as FormValues;

	const periodOptions = gamePeriods.map((gp) => ({ key: gp.key, value: gp.key, label: gp.label} as Option));

	return (
		<Form
			initialValues={exportFormValues}
			onSubmit={onSubmit}
			validate={onValidate}
			render={({ handleSubmit, valid}) => (
				<form onSubmit={handleSubmit} className="team-stats-export">
					<div className="team-stats-export__title">{_('stats.export.title')}</div>
					<Field name="format">
						{({input, meta}) => (
							<div className="team-stats-export__formats">
								<span key="csv">
									<input id={`export-format-csv`} type="radio" {...input} value="csv"/>
									<label htmlFor={`export-format-csv`}>
										<Badge
											label=".csv"
											variant={input.value === "csv" ? "secondary" : "default"}
											outline={input.value !== "csv"}
											className="team-stats-export__format"
											onClick={() => {}}
											block={true}
										/>
									</label>
								</span>
								<span key="xlsx">
									<input id={`export-format-xlsx`} type="radio" {...input} value="xlsx"/>
									<label htmlFor={`export-format-xlsx`}>
										<Badge
											label=".xlsx"
											variant={input.value === "xlsx" ? "secondary" : "default"}
											outline={input.value !== "xlsx"}
											onClick={() => {}}
											className="team-stats-export__format"
											block={true}
										/>
									</label>
								</span>
							</div>
						)}
					</Field>
					<div className="event-export__title">{_('stats.export.periods')}</div>
					<div className="event-export__blocks">
						{periodOptions.map((period: Option, index) => (
							<Field name={`periods[${index}]`} key={`period-${period.key}`}>
								{({input, meta}) => <div key={period.key}>
									<input id={`export-period-${period.key}`} type="checkbox" {...input}
										   checked={!!input.value}/>
									<label htmlFor={`export-period-${period.key}`}>
										<Badge
											label={period.label}
											variant={input.value === true ? "secondary" : "default"}
											outline={!input.value} block={true}
											icon={!!input.value ? "check" : undefined}
											onClick={() => {}}
										/>
									</label>
								</div>}
							</Field>
						))}
					</div>
					<div className="team-stats-export__title">{_('stats.export.statsCategories')}</div>
					<div className="team-stats-export__blocks">
						{availableCategories.map((category: CategoryEnum) => (
							<Field name={category.toString()} key={`category-${category}`}>
								{({input, meta}) => (
									<div key={category}>
										<input type="checkbox" id={`export-category-${category}`} {...input} />
										<label htmlFor={`export-category-${category}`}>
											<Badge
												label={_any(`gamePage.stats.category.${category}`)}
												variant={input.value === true ? "secondary" : "default"}
												outline={!input.value}
												block={true}
												icon={!!input.value ? "check" : undefined}
												onClick={() => {}}
											/>
										</label>
									</div>
								)}
							</Field>
						))}
					</div>
					<button type="submit" disabled={!valid}>
						<Badge label="stats.export.submit" block={true} variant="primary" onClick={() => {}} disabled={!valid} />
					</button>
				</form>
			)}
		/>
	)
}
